import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { STATUS, TIMEOUT_DOUBLED } from '@app/constants/commonConstants';
import useHttp from '../useHttp';
import { replaceTokensInString } from '@app/utils';
import endpoints from '@app/constants/endpoints';
import { loadFilteredData } from '@app/store/myequipment/utils';

/**
 * This hook is used to fetch cat and non-cat equipments and stores data in local state.
 * @returns {Object} returns invoking function, data with cat and non-cat equipments and helios equipments.
 */
const useAllEquipment = () => {
  const getCommonVarsStatus = useSelector(s => s.common.getCommonVarsStatus);
  const isCommonApiLoaded =
    getCommonVarsStatus === STATUS.RESOLVED ||
    getCommonVarsStatus === STATUS.REJECTED;
  const langId = useSelector(state => state.common?.langId);
  const userId = useSelector(state => state.common?.userId);
  const isBuyOnBehalf = useSelector(state => state.common?.isBuyOnBehalf);
  const storeId = useSelector(state => state.common?.storeId);
  const selectedCustomerNumber = useSelector(
    state => state.common?.selectedCustomerNumber
  );
  const [myEquipmentListData, setMyEquipmentListData] = useState([]);
  const {
    data = {},
    error,
    invoke,
    loading
  } = useHttp({ timeout: TIMEOUT_DOUBLED });
  const emptyEquipmentResult = {
    mccResponse: true,
    myEquipmentList: [],
    selectedEquipment: {},
    isHelios: false
  };
  const getMyEquipmentData = useCallback(async () => {
    let url = replaceTokensInString(
      endpoints.GET_MY_EQUIPMENT,
      storeId,
      langId
    );
    url +=
      isBuyOnBehalf && userId
        ? `&forUserId=${userId}&includeNonCatEquipment=true`
        : `&includeNonCatEquipment=true`;
    url = encodeURI(url);
    try {
      const result = await invoke({ rethrowError: true, url });
      if (result) {
        const equipments =
          Object.keys(result).length > 0 &&
          Object.keys(result?.myEquipmentList).length > 0
            ? result
            : emptyEquipmentResult;
        let equipmentData = equipments?.myEquipmentList;
        if (equipments?.isHelios) {
          const { myEquipmentList } = loadFilteredData(
            equipments,
            selectedCustomerNumber
          );
          equipmentData = myEquipmentList;
        }
        setMyEquipmentListData(equipmentData);
      }
    } catch {
      setMyEquipmentListData([]);
    }
  }, [invoke, isBuyOnBehalf, langId, storeId, userId]);

  useEffect(() => {
    if (isCommonApiLoaded) {
      getMyEquipmentData();
    }
  }, [isCommonApiLoaded]);
  return {
    invoke: getMyEquipmentData,
    data: { allEquipmentData: data, myEquipmentListData },
    error,
    loading
  };
};

export default useAllEquipment;
